import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { useCanViewFinancials } from '../hooks/useCanViewFinancials';

const STATUSES = [
  { key: 'New', color: '#64748b' },
  { key: 'In Progress', color: '#3b82f6' },
  { key: 'Pitched', color: '#06b6d4' },
  { key: 'Won', color: '#22c55e' },
  { key: 'Lost', color: '#6b7280' },
  { key: 'On Hold', color: '#a855f7' },
];

const PRIORITY_COLORS = { Critical: '#ef4444', High: '#f97316', Medium: '#eab308', Low: '#64748b' };

function fmt(v) {
  if (!v && v !== 0) return '';
  if (Math.abs(v) >= 100) return '\u20b9' + (v / 100).toFixed(1) + 'Cr';
  return '\u20b9' + Number(v).toFixed(0) + 'L';
}

function StatusColumn({ status, briefs, onDrop, onCardClick, canViewFinancials }) {
  const [dragOver, setDragOver] = useState(false);
  const total = briefs.reduce((s, b) => s + (b.opportunity_value_lakhs || 0), 0);

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={e => { e.preventDefault(); setDragOver(false); const id = e.dataTransfer.getData('briefId'); if (id) onDrop(id, status.key); }}
      style={{
        flex: '0 0 auto', minWidth: 220, width: 260,
        background: dragOver ? 'rgba(255,255,255,.03)' : 'var(--s1)',
        border: `1px solid ${dragOver ? status.color + '44' : 'var(--bdr)'}`,
        borderRadius: 10, padding: '12px 10px', transition: 'all .15s',
        display: 'flex', flexDirection: 'column', gap: 6,
      }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 8, borderBottom: '1px solid var(--bdr)', marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: status.color }} />
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700, letterSpacing: '.1em', color: 'var(--t1)' }}>{status.key.toUpperCase()}</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--t3)', background: 'var(--s2)', padding: '1px 6px', borderRadius: 8 }}>{briefs.length}</span>
        </div>
        {canViewFinancials && total > 0 && (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: status.color }}>{fmt(total)}</span>
        )}
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, minHeight: 60, overflowY: 'auto', maxHeight: 'calc(100vh - 240px)' }}>
        {briefs.length === 0 && (
          <div style={{ padding: 16, textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--t3)', opacity: .5 }}>Drop briefs here</div>
        )}
        {briefs.map(b => (
          <div key={b._id || b.id}
            draggable onDragStart={e => e.dataTransfer.setData('briefId', b._id || b.id)}
            onClick={() => onCardClick(b._id || b.id)}
            style={{
              background: 'var(--s2)', border: '1px solid var(--bdr)',
              borderLeft: `3px solid ${PRIORITY_COLORS[b.priority] || '#64748b'}`,
              borderRadius: 8, padding: '10px 10px', cursor: 'grab',
              transition: 'all .12s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = status.color + '44'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--bdr)'; e.currentTarget.style.transform = 'none'; }}
          >
            <div style={{ fontFamily: 'var(--font-sans)', fontSize: 11, fontWeight: 600, color: 'var(--t1)', marginBottom: 4, lineHeight: 1.3 }}>{b.title || 'Untitled'}</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--t3)', marginBottom: 6 }}>{b.company_name || b.lead_name || ''}</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              {b.service_type && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 8, padding: '2px 6px', borderRadius: 4, background: 'var(--s3)', color: 'var(--t2)' }}>{b.service_type}</span>}
              {canViewFinancials && b.opportunity_value_lakhs > 0 && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 8, color: 'var(--t2)' }}>{fmt(b.opportunity_value_lakhs)}</span>}
              {b.pitch_date && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 8, color: 'var(--t3)' }}>{new Date(b.pitch_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>}
            </div>
            {b.assigned_to_name && <div style={{ fontFamily: 'var(--font-mono)', fontSize: 8, color: 'var(--t3)', marginTop: 5, opacity: .7 }}>\u2192 {b.assigned_to_name}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];

export default function BriefsKanban() {
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [team, setTeam] = useState([]);
  const navigate = useNavigate();
  const canViewFinancials = useCanViewFinancials();

  useEffect(() => { load(); loadTeam(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/bizdev/briefs');
      setBriefs((res.data || []).filter(b => !b.deleted_at));
    } catch (e) { console.error('Failed to load briefs', e); }
    setLoading(false);
  };

  const loadTeam = async () => {
    try {
      const res = await api.get('/users');
      setTeam(res.data || []);
    } catch (e) {}
  };

  const handleDrop = async (briefId, newStatus) => {
    const prevBriefs = briefs;
    setBriefs(prev => prev.map(b => (b._id || b.id || b.brief_id) === briefId ? { ...b, status: newStatus } : b));
    try {
      await api.put('/bizdev/briefs/' + briefId, { status: newStatus });
    } catch (e) {
      // Surface the real backend reason — e.g. "Revenue Generated (₹ Lakhs)
      // is required to mark as Won/Executed" — instead of silently reverting.
      const detail = e?.response?.data?.detail || e?.message || 'Failed to update brief status';
      console.error('Failed to update brief status', e?.response?.data || e);
      if (typeof window !== 'undefined' && window.alert) window.alert(detail);
      setBriefs(prevBriefs);
    }
  };

  const sl = search.toLowerCase();
  const filtered = briefs.filter(b => {
    if (sl && !(b.title || '').toLowerCase().includes(sl) && !(b.company_name || '').toLowerCase().includes(sl)) return false;
    if (priorityFilter && b.priority !== priorityFilter) return false;
    if (assigneeFilter && b.assigned_to !== assigneeFilter) return false;
    return true;
  });

  const grouped = {};
  STATUSES.forEach(s => { grouped[s.key] = []; });
  filtered.forEach(b => {
    const key = STATUSES.find(s => s.key === b.status)?.key || 'New';
    if (grouped[key]) grouped[key].push(b);
  });

  const btnStyle = (active) => ({ fontFamily: 'var(--font-mono)', fontSize: 9, padding: '5px 10px', background: active ? 'rgba(227,25,55,.15)' : 'var(--s2)', border: `1px solid ${active ? 'rgba(227,25,55,.3)' : 'var(--bdr)'}`, borderRadius: 6, color: active ? 'var(--r)' : 'var(--t3)', cursor: 'pointer' });

  return (
    <div style={{ padding: '0 16px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-orb)', fontSize: 16, fontWeight: 700, color: 'var(--t1)', margin: 0 }}>Briefs Board</h2>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--t3)', margin: '2px 0 0' }}>Drag briefs between statuses {'\u00b7'} {filtered.length}/{briefs.length} shown</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => navigate('/bizdev/briefs')} style={btnStyle(false)}>List View</button>
          <button onClick={() => navigate('/bizdev/briefs/new')} style={{ ...btnStyle(false), background: 'rgba(227,25,55,.08)', border: '1px solid rgba(227,25,55,.2)', color: 'var(--r)' }}>+ New Brief</button>
          <button onClick={load} style={btnStyle(false)}>{loading ? '\u21bb' : '\u21bb Refresh'}</button>
        </div>
      </div>
      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search briefs..."
          style={{ fontFamily: 'var(--font-mono)', fontSize: 10, padding: '5px 10px', background: 'var(--s2)', border: '1px solid var(--bdr)', borderRadius: 6, color: 'var(--t1)', width: 180 }} />
        <select value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)}
          style={{ fontFamily: 'var(--font-mono)', fontSize: 9, padding: '5px 8px', background: 'var(--s2)', border: '1px solid var(--bdr)', borderRadius: 6, color: 'var(--t2)' }}>
          <option value="">All Assignees</option>
          {team.map(u => <option key={u.user_id || u.id} value={u.user_id || u.id}>{u.name || u.email}</option>)}
        </select>
        <button onClick={() => setPriorityFilter('')} style={btnStyle(!priorityFilter)}>All</button>
        {PRIORITIES.map(p => (
          <button key={p} onClick={() => setPriorityFilter(priorityFilter === p ? '' : p)} style={btnStyle(priorityFilter === p)}>{p}</button>
        ))}
      </div>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--t3)' }}>{'\u26a1'} Loading briefs...</div>
      ) : (
        <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 8 }}>
          {STATUSES.map(s => (
            <StatusColumn key={s.key} status={s} briefs={grouped[s.key]} onDrop={handleDrop}
              onCardClick={id => navigate('/bizdev/briefs/' + id)} canViewFinancials={canViewFinancials} />
          ))}
        </div>
      )}
    </div>
  );
}
